/*
 * @Description: 时间处理函数集
 * @Author: mdb
 * @Date: 2023-03-02 16:45:09
 * @LastEditTime: 2023-03-14 13:44:19
 * @LastEditors: mdb
 */
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type { ConfigType } from 'dayjs'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * 北京时区
 */
const beijingTimeZone = 'Asia/Shanghai'

/**
 * 默认格式
 */
const FORMAT_STR = 'YYYY/MM/DD HH:mm:ss'

/**
 * 获取当前时区
 * @returns 当前时区
 */
export const getCurrentTimeZone = (): string => dayjs.tz.guess()

/**
 * 将所有时间转为北京时间
 * @param value 需要格式化的时间
 * @param formatStr 返回格式
 * @returns 北京时间
 */
export const toBeijingTime = (value: ConfigType = undefined, formatStr = FORMAT_STR): string => {
  // 如果 value 为字符串，且包含 Y、M、D、H、h、m 之一，则返回当前时间，value 为格式
  const isFormat =
    typeof value === 'string' &&
    (value.includes('Y') ||
      value.includes('M') ||
      value.includes('D') ||
      value.includes('H') ||
      value.includes('h') ||
      value.includes('m'))
  try {
    return isFormat ? dayjs().tz(beijingTimeZone).format(value) : dayjs(value).tz(beijingTimeZone).format(formatStr)
  } catch (error) {
    console.error('toBeijingTime error:', error)
    return ''
  }
}

/**
 * 获取当月的开始时间、今天时间、结束时间
 * @param timeType 时间类型：'currentTime' 当前时分秒，'lastTime'  23:59:59
 * @param formatStr 返回格式
 * @returns 多个日期时间对象
 */
export const getCurrentMonthDateRange = (timeType: 'currentTime' | 'lastTime' = 'lastTime', formatStr = FORMAT_STR) => {
  const now = dayjs()
  const timeEnum = {
    currentTime: now.format(formatStr),
    lastTime: now.endOf('day').format(formatStr)
  }
  const currentDayOfMonth = timeEnum[timeType]
  const firstDayOfMonth = now.startOf('month').format(formatStr)
  const lastDayOfMonth = now.endOf('month').format(formatStr)
  return { firstDayOfMonth, currentDayOfMonth, lastDayOfMonth }
}
/**
 * 获取某个月的开始时间、今天时间、结束时间,不传默认是当月
 * @param monthDayjs 月日期
 * @param formatStr 返回格式
 * @returns 多个日期时间对象
 */
export const getMonthDays = (monthDayjs?: Dayjs, formatStr?: string) => {
  let currentMonth
  if (!monthDayjs) {
    currentMonth = dayjs()
  } else {
    currentMonth = monthDayjs
  }
  const firstDayOfMonth = currentMonth.startOf('month').format(formatStr)
  const lastDayOfMonth = currentMonth.endOf('month').format(formatStr)
  return { firstDayOfMonth, lastDayOfMonth }
}
/**
 * 默认13周的区间 参数目前不需要
 * @returns 返回默认13周的区间
 */
export const getDefaultWeekRange = () => {
  let startTime: string
  const endTime = dayjs().format('YYYYww')

  const [endYear, endWeek] = [parseInt(endTime.slice(0, 4)), parseInt(endTime.slice(4, 6))]
  /// 判断当前week是否大于13周，大于那么还是当前年份，只需要week减去13
  if (endWeek > 13) {
    startTime = `${endYear}${String(endWeek - 13).padStart(2, '0')}`
  } else {
    const startYear = endYear - 1
    startTime = `${startYear}${String(endWeek - 13 + 52).padStart(2, '0')}`
  }
  return { startTime, endTime }
}
// 获取今天所在周的第一天和最后一天
export const getCurrentWeekRange = () => {
  const now = new Date()
  const dayOfWeek = now.getDay() // 获取当前是周几（0表示周日，1表示周一，依此类推）
  const startOfWeek = new Date(now) // 创建当前时间的副本
  const endOfWeek = new Date(now) // 创建当前时间的副本

  // 计算周一和周日的日期
  const diffToMonday = (dayOfWeek === 0 ? 7 : dayOfWeek) - 1 // 周日特殊处理
  const diffToSunday = 7 - dayOfWeek

  // 设置为周一的日期并固定时分秒为 00:00:00
  startOfWeek.setDate(now.getDate() - diffToMonday)
  startOfWeek.setHours(0, 0, 0, 0) // 时分秒设为 00:00:00 如果想以当前时间那么可以删去本行

  // 设置为周日的日期并固定时分秒为 23:59:59
  endOfWeek.setDate(now.getDate() + diffToSunday)
  endOfWeek.setHours(23, 59, 59, 999) // 时分秒设为 23:59:59

  // 格式化为 YYYY/MM/DD HH:mm:ss
  const formatDateTime = (date: Date) => {
    const yyyy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, '0') // 月份从0开始
    const dd = String(date.getDate()).padStart(2, '0')
    const hh = String(date.getHours()).padStart(2, '0')
    const min = String(date.getMinutes()).padStart(2, '0')
    const ss = String(date.getSeconds()).padStart(2, '0')

    return `${yyyy}/${mm}/${dd} ${hh}:${min}:${ss}`
  }

  return {
    startOfWeek: formatDateTime(startOfWeek), // 返回周一的开始时间
    endOfWeek: formatDateTime(endOfWeek) // 返回周日的结束时间
  }
}
